import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  Check,
  Building2,
  FileCheck,
  Search,
  Zap,
  Star,
  MessageSquare,
  Eye,
  AlertTriangle,
  RotateCcw,
  UserCheck,
} from "lucide-react";

export const Route = createFileRoute("/services_/orm")({
  head: () => ({
    meta: [
      {
        title:
          "Online Reputation Management (ORM) Services — KMWAdvertising · Brand Protection",
      },
      {
        name: "description",
        content:
          "Online Reputation Management (ORM) Services That Build Trust, Protect Brands and Strengthen Digital Presence. Review Management & Social Listening by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Online Reputation Management (ORM) Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Protect Your Brand Reputation in the Digital Age. Proactive review management, Search Reputation (SERP), crisis recovery, and executive branding.",
      },
    ],
  }),
  component: ORMServicePage,
});

export function ORMServicePage() {
  const whyORMMatters = [
    {
      title: "Build Customer Trust",
      desc: "Positive online ratings and customer experiences reassure prospective buyers and increase confidence in your products and services.",
      icon: UserCheck,
    },
    {
      title: "Improve Search Visibility",
      desc: "Publishing authoritative, high-quality content elevates positive search results while suppressing misleading information.",
      icon: Search,
    },
    {
      title: "Protect Brand Equity",
      desc: "Continuous automated monitoring identifies potential reputation risks and viral negative sentiment before issues escalate.",
      icon: ShieldCheck,
    },
    {
      title: "Increase Customer Loyalty",
      desc: "Responding professionally to feedback demonstrates transparency, customer care, and an active commitment to service quality.",
      icon: MessageSquare,
    },
    {
      title: "Support Business Revenue",
      desc: "A clean digital footprint directly contributes to higher lead inquiries, stronger conversion rates, and better enterprise valuation.",
      icon: TrendingUp,
    },
  ];

  const monitoringChannels = [
    "Google & Bing Search Engine Results (SERPs)",
    "National & Regional Digital News Media",
    "Social Media Networks (Meta, X, LinkedIn, IG)",
    "Review Sites (Google Business, Trustpilot, Glassdoor)",
    "Industry Blogs & Niche Publications",
    "Consumer Complaint Forums & Reddit Communities",
    "Business Directory Listings",
    "YouTube & Video Comments",
  ];

  const reviewManagementFeatures = [
    "24/7 Automated Review Monitoring & Alerts",
    "Professional Customer Response Management",
    "Google Business Profile (GBP) Review Strategy",
    "Industry-Specific Review Platform Management",
    "Ethical White-Hat Review Generation Campaigns",
    "Customer Feedback & CSAT Survey Workflows",
    "Transparent Review Trend Reporting",
    "Strict Zero-Fake-Review Ethical Guidelines",
  ];

  const searchReputationSuite = [
    {
      title: "Positive Content Development",
      desc: "Creating authoritative articles, corporate profiles, and press assets that occupy page-one search positions.",
      icon: FileCheck,
    },
    {
      title: "SEO-Driven SERP Suppression",
      desc: "Optimizing high-domain digital assets to naturally push down unfavorable or inaccurate search results.",
      icon: Search,
    },
    {
      title: "Knowledge Panel Optimisation",
      desc: "Claiming and enhancing Google Knowledge Panels with verified organizational data and brand links.",
      icon: Star,
    },
    {
      title: "Digital PR & Press Distribution",
      desc: "Distributing newsworthy corporate updates across tier-1 news portals to build lasting domain authority.",
      icon: Globe,
    },
  ];

  const crisisRecoverySteps = [
    "Rapid Reputation Impact Assessment",
    "Stakeholder & Media Communication Guidance",
    "Positive Authority Content Surge",
    "SERP SEO Enhancement & Suppression",
    "Digital PR & Official Statement Support",
    "Long-Term Brand Trust Recovery Plan",
  ];

  const corporateBrandingPoints = [
    "C-Suite & Executive Profile Optimisation",
    "CEO & Founder Leadership Branding",
    "Employer Brand & Glassdoor Management",
    "Investor Relations & Media Monitoring",
    "Corporate Communications Strategy",
    "Industry Thought Leadership Content",
  ];

  const ormProcessSteps = [
    { num: "01", title: "Reputation Audit", desc: "Baseline audit of search results, review ratings, news articles, and social sentiment." },
    { num: "02", title: "Monitoring Setup", desc: "Configuring real-time brand listening tools across web, search, news, and social feeds." },
    { num: "03", title: "Strategic ORM Plan", desc: "Formulating review management workflows, positive asset creation, and crisis protocols." },
    { num: "04", title: "Content & SEO Execution", desc: "Publishing authoritative content, optimizing profiles, and building digital PR assets." },
    { num: "05", title: "Reporting & Sentiment Analysis", desc: "Delivering regular executive reports tracking sentiment shifts, rating gains, and SERP control." },
  ];

  const industriesServed = [
    "Healthcare & Hospitals",
    "Hospitality & Hotels",
    "Higher Education & Universities",
    "Financial Services & Banking",
    "E-commerce & Retail Brands",
    "Real Estate & Developers",
    "Technology & Enterprise SaaS",
    "FMCG & Consumer Goods",
  ];

  const kmwaStrengths = [
    "25+ years of strategic media & communications experience",
    "Certified ORM & SEO reputation strategists",
    "100% ethical, white-hat review management practices",
    "AI-powered social listening & sentiment tracking",
    "Integrated Digital PR & SERP suppression capabilities",
    "Transparent reporting with clear sentiment tracking",
    "Crisis management & media guidance expertise",
    "Executive & employer branding solutions",
    "Multi-platform brand protection playbooks",
    "Long-term partnership focused on authentic trust",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">Online Reputation Management (ORM)</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <ShieldCheck className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Online Reputation Management (ORM) Services That Build Trust, Protect Brands and Strengthen Digital Presence
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                Online Reputation Management (ORM) & Brand Protection
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Protect Your Brand Reputation in the Digital Age
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Your online reputation influences how customers, investors, employees and business partners perceive your brand. A single negative review, misleading article or unfavourable social media discussion can affect purchasing decisions, reduce customer confidence and impact long-term business growth.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  Today, people research brands before making decisions. They read reviews, compare ratings, search news, and evaluate social media conversations. This makes Online Reputation Management an essential pillar of digital marketing.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we help businesses proactively manage, monitor and strengthen their online reputation across search engines, review platforms, social media, news sites, and digital communities.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request ORM Audit & Brand Protection
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY ORM MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Brand Value & Customer Trust
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Online Reputation Management Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Reputation is one of your most valuable business assets. Consumers rely heavily on digital trust signals before choosing a product, company, or service.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyORMMatters.map((item, idx) => {
                const OIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <OIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ORM MONITORING & REVIEW MANAGEMENT */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Proactive Brand Protection
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Online Reputation Management Services
              </h2>
            </div>

            {/* MONITORING & REVIEWS GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* MONITORING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <Eye className="h-3.5 w-3.5" />
                    24/7 Social Listening
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Brand & Review Monitoring</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Stay Informed About Every Brand Mention Across Web & Social
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our real-time monitoring tools track conversations early, helping you respond promptly and maintain complete control over brand perception.
                </p>

                <div className="space-y-2 pt-2">
                  {monitoringChannels.map((mc, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{mc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* REVIEW MANAGEMENT */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 mb-3">
                    <Star className="h-3.5 w-3.5" />
                    Authentic Ratings Growth
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Review & Feedback Management</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Build Trust Through Authentic Customer Feedback
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We encourage ethical, white-hat review generation campaigns and response workflows to elevate ratings across Google and industry portals.
                </p>

                <div className="space-y-2 pt-2">
                  {reviewManagementFeatures.map((rmf, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                      <span>{rmf}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* SEARCH REPUTATION MANAGEMENT SUITE */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  Search Reputation Management (SERP Control)
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Improve what people see when they search for your company, brand, or executive leadership on Google.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {searchReputationSuite.map((srs, idx) => {
                  const SIcon = srs.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <SIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{srs.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{srs.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* REPUTATION RECOVERY & CORPORATE BRANDING */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Crisis Recovery */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <RotateCcw className="h-5 w-5 text-[var(--glow-purple)]" />
                  Reputation Recovery & Crisis Strategy
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Structured communication frameworks to rebuild trust and recover from misinformation or negative publicity:
                </p>
                <div className="space-y-2 pt-2">
                  {crisisRecoverySteps.map((crs, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{crs}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Corporate Branding */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-sky-400" />
                  Corporate & C-Suite Executive Branding
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Strengthening digital presence and thought leadership for founders, executives, and enterprise organizations:
                </p>
                <div className="space-y-2 pt-2">
                  {corporateBrandingPoints.map((cbp, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{cbp}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ORM PROCESS & INDUSTRIES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Structured Audit & Framework
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our 5-Step ORM Process
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-16">
              {ormProcessSteps.map((step, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">{step.num}</span>
                  <h5 className="text-sm font-bold text-white">{step.title}</h5>
                  <p className="text-xs text-muted-foreground leading-snug">{step.desc}</p>
                </div>
              ))}
            </div>

            <div className="p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90">
              <h4 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-4 block">
                Industries We Protect:
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {industriesServed.map((ind, i) => (
                  <div key={i} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 text-xs text-white/90 font-medium text-center">
                    {ind}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Media & PR Heritage
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <ShieldCheck className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              ORM & Brand Protection Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Protect and Strengthen Your Online Reputation Today?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our ORM and brand reputation specialists for a confidential reputation audit and proactive digital protection strategy tailored to your goals.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Confidential ORM Audit
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
